import React from 'react';

import { useDisciplines } from '@modules/Disciplines/hooks/useDisciplines';
import { useMajors } from '@modules/Majors/hooks/useMajors';
import { useUniversities } from '@modules/Universities/hooks/useUniversities';
import { useAssertions } from '@modules/Assertions/hooks/useAssertions';

import { ANSWERED_ASSERTIONS_LIMIT, REQUIRED_ANSWERS, TOP_MATCHES } from '../config';
import { buildCircles, buildPlotted } from '../helpers/plotMatches';

export const useUnderstandMatches = (): Record<Matches.Category, Matches.CategoryData> => {
  const { disciplines } = useDisciplines({ limit: TOP_MATCHES });
  const { majors } = useMajors({ limit: TOP_MATCHES });
  const { universities } = useUniversities({ limit: TOP_MATCHES });
  const { assertions, count: totalAnswered } = useAssertions({
    answered: true,
    limit: ANSWERED_ASSERTIONS_LIMIT,
  });

  return React.useMemo(() => {
    const build = (
      category: Matches.Category,
      circles: Matches.Circle[],
      getLinks: (assertion: Assertions.Assertion) => Assertions.LinkedEntity[],
    ): Matches.CategoryData => ({
      category,
      circles,
      assertions: buildPlotted(circles, assertions, getLinks),
      isComplete: totalAnswered >= REQUIRED_ANSWERS[category],
      requiredAnswers: REQUIRED_ANSWERS[category],
      totalAnswered,
    });

    return {
      disciplines: build(
        'disciplines',
        buildCircles(disciplines, item => item.title),
        assertion => assertion.disciplines ?? [],
      ),
      majors: build(
        'majors',
        buildCircles(majors, item => item.title),
        assertion => assertion.majors ?? [],
      ),
      universities: build(
        'universities',
        buildCircles(universities, item => item.name),
        assertion => assertion.universities ?? [],
      ),
    };
  }, [disciplines, majors, universities, assertions, totalAnswered]);
};
